import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { Settings } from "@mui/icons-material";
import { useState, useEffect, useContext } from "react";
import axios from "axios";
import CustomSnackbar from "components/CustomSnackbar";
import DotPulseLoading from "components/Loading/DotPulseLoading";
import { useTranslation } from "react-i18next";
import { AuthContext } from "auth/AuthContext";

const SettingsForm = () => {
  const { user, updateUserData } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    sso_setting: user?.isSsoLogin || false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Update settings jika user berubah
  useEffect(() => {
    if (user) {
      setSettings({
        sso_setting: user.isSsoLogin || false,
      });
    }
  }, [user]);

  const handleChange = (event) => {
    setSettings({
      ...settings,
      [event.target.name]: event.target.checked,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Unauthorized: No token found");
      }

      const lang = localStorage.getItem("i18nextLng") || "en";
      const payload = {
        ssoSetting: settings.sso_setting,
      };
      // Preserve other fields
      if (user?.profileImageUrl) {
        payload.profileImageUrl = user.profileImageUrl;
      }

      // Request ke backend
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/auth/profile`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": lang,
          },
        }
      );

      // Update context
      updateUserData({ isSsoLogin: settings.sso_setting });

      setSnackbar({
        open: true,
        message: response.data.message || "Updated successfully.",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {t("Dashboard_profile.settings.accountSettings")}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={settings.sso_setting}
              onChange={handleChange}
              name="sso_setting"
              color="primary"
            />
          }
          label={t("Dashboard_profile.settings.enableSSO")}
        />
        <Box sx={{ my: 1 }} />

        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-end",
            "& .MuiButton-root": {
              width: { xs: "100%", sm: "auto" },
            },
          }}
        >
          <Button
            type="submit"
            variant="contained"
            startIcon={<Settings />}
            size="medium"
            disabled={loading}
          >
            {loading
              ? t("Dashboard_profile.settings.saving")
              : t("Dashboard_profile.settings.save")}
          </Button>
        </Box>
      </form>

      {/* Modern Snackbar for notifications */}
      <CustomSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />

      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DotPulseLoading size={14} color="#3f51b5" />
        </Box>
      )}
    </>
  );
};

export default SettingsForm;
